import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, ChevronDown, ClipboardCheck, HeartPulse, Dumbbell, Trophy,
  Stethoscope, Activity, LineChart, TrendingUp, Compass, Users, Smartphone,
  Check, MessageSquare, Quote, Sparkles,
} from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { WhatsAppCTA } from "@/components/WhatsApp";
import { DEFAULT_WA_MESSAGES } from "@/config/site";
import { usePageCms, resolveAssetUrl } from "@/lib/siteSettings";

const HERO = "https://images.unsplash.com/photo-1696536823512-79d724454616?crop=entropy&cs=srgb&fm=jpg&q=85";
const FACILITY = "https://images.unsplash.com/photo-1540497077202-7c8a3999166f?crop=entropy&cs=srgb&fm=jpg&q=85";

const BOOK = "/prices";
const FIND = "/service-finder";

export default function Landing() {
  const { t } = useTranslation();
  const heroBgRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const cms = usePageCms("home");

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        setScrolled(y > 40);
        if (heroBgRef.current) {
          heroBgRef.current.style.transform = `translate3d(0, ${y * 0.3}px, 0) scale(${1 + Math.min(y, 400) * 0.0003})`;
        }
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !("IntersectionObserver" in window)) return;
    const fadeUp = Array.from(document.querySelectorAll("[data-reveal]"));
    fadeUp.forEach((el, i) => { el.classList.add("fl-reveal"); el.style.transitionDelay = `${(i % 4) * 70}ms`; });
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in-view"); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    fadeUp.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [cms]);

  const reduced = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const heroImg = resolveAssetUrl(cms.get("hero").mediaUrl) || HERO;
  const heroVideo = resolveAssetUrl(cms.get("hero").videoUrl);
  const platformImg = resolveAssetUrl(cms.get("platform").mediaUrl);
  const cliniciansImg = resolveAssetUrl(cms.get("clinicians").mediaUrl) || FACILITY;

  const journeyItems = cms.items("journey", [
    { label: "Assess", body: "Understand the problem, your current capacity and your goals." },
    { label: "Restore", body: "Reduce limitations and restore confident, pain-free movement." },
    { label: "Build", body: "Develop the strength, tolerance and capacity daily life and sport demand." },
    { label: "Perform", body: "Return to the activity, training and sport you care about — with confidence." },
  ]);
  const whyItems = cms.items("why", [
    { label: "Clinical reasoning", body: "Care built around assessment, your goals and your individual needs — never a template." },
    { label: "Progressive rehabilitation", body: "Treatment connects directly into exercise and performance, so progress keeps going." },
    { label: "Objective measurement", body: "Progress is tracked with movement, strength and functional outcomes — not guesswork." },
  ]);
  const platformSteps = cms.items("platform", [
    { label: "Check in" }, { label: "Train" }, { label: "Track" }, { label: "Adjust" },
  ]);
  const startItems = cms.items("starts", [
    { label: "I have pain or an injury", body: "Start with a clinical assessment to understand what's going on.", ctaLabel: "Book an assessment", ctaPath: BOOK },
    { label: "I'm recovering or rehabilitating", body: "Find the right pathway to keep your recovery moving forward.", ctaLabel: "Find the right pathway", ctaPath: FIND },
    { label: "I want to return to sport or perform", body: "Rebuild strength, confidence and sport-specific capacity.", ctaLabel: "Explore performance support", ctaPath: "/services" },
  ]);
  const supportItems = cms.items("support", [
    { label: "Services", body: "One-off and specialist sessions for assessment, treatment and testing.", ctaLabel: "View services", ctaPath: "/services" },
    { label: "Memberships", body: "Ongoing clinician support on a simple monthly plan.", ctaLabel: "See memberships", ctaPath: "/prices" },
    { label: "Rehabilitation programmes", body: "Structured, clinician-led pathways tailored after your assessment.", ctaLabel: "Explore programmes", ctaPath: "/prices" },
    { label: "Online rehabilitation", body: "Guided remote rehab you can follow from anywhere.", ctaLabel: "Go online", ctaPath: "/prices" },
  ]);

  const JOURNEY_ICONS = [ClipboardCheck, HeartPulse, Dumbbell, Trophy];
  const WHY_ICONS = [Stethoscope, Activity, LineChart];
  const START_ICONS = [HeartPulse, Compass, Trophy];
  const SUPPORT_ICONS = [Activity, Users, ClipboardCheck, Smartphone];
  const STEP_ICONS = [ClipboardCheck, Dumbbell, TrendingUp, Compass];

  const topLine = { background: "linear-gradient(90deg, transparent, color-mix(in oklab, var(--fl-orange) 45%, transparent), transparent)" };

  const SECTIONS = [
    // ---- Assess → Restore → Build → Perform ----
    cms.isVisible("journey") && {
      key: "journey", default: 0,
      node: (
        <section key="journey" id="journey" className="py-24 md:py-36 relative border-t border-white/5" style={{ background: "var(--fl-bg)" }} data-testid="home-section-journey">
          <div className="absolute top-0 left-0 right-0 h-px" style={topLine} />
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-3xl">
              <div className="fl-label text-[color:var(--fl-orange)] mb-3">{cms.text("journey", "eyebrow", "Your pathway")}</div>
              <h2 data-reveal className="fl-heading text-4xl md:text-5xl leading-tight">{cms.text("journey", "heading", "Assess. Restore. Build. Perform.")}</h2>
              <p className="mt-5 text-base md:text-lg" style={{ color: "var(--fl-muted)" }}>{cms.text("journey", "body", "First we understand where you are. Then we build the right pathway forward.")}</p>
            </div>
            <div className="mt-14 grid md:grid-cols-4 gap-px rounded-lg overflow-hidden" style={{ background: "var(--fl-border)" }}>
              {journeyItems.filter((it) => it.visible).map((it, i) => {
                const Icon = JOURNEY_ICONS[i] || Activity;
                return (
                  <div key={i} data-reveal className="p-7 md:p-8 relative group transition-colors" style={{ background: "var(--fl-surface, #121212)" }} data-testid={`journey-stage-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className="fl-heading text-5xl md:text-6xl leading-none text-white/10 group-hover:text-[color:var(--fl-orange)]/30 transition-colors">0{i + 1}</span>
                      <Icon className="text-[color:var(--fl-orange)] ml-auto" size={22} />
                    </div>
                    <div className="fl-heading text-xl md:text-2xl mt-5 uppercase tracking-tight">{it.label}</div>
                    <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--fl-muted)" }}>{it.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ),
    },
    // ---- Why Functional LAB (3 differentiators) ----
    cms.isVisible("why") && {
      key: "why", default: 1,
      node: (
        <section key="why" id="why" className="py-24 md:py-36 relative border-t border-white/5" style={{ background: "var(--fl-surface, #121212)" }} data-testid="home-section-why">
          <div className="absolute top-0 left-0 right-0 h-px" style={topLine} />
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
              <div className="max-w-xl">
                <div className="fl-label text-[color:var(--fl-orange)] mb-3">{cms.text("why", "eyebrow", "Why Functional LAB")}</div>
                <h2 data-reveal className="fl-heading text-4xl md:text-5xl leading-tight">{cms.text("why", "heading", "Rehabilitation with a reason behind every step.")}</h2>
              </div>
              <p className="md:text-right max-w-sm text-sm" style={{ color: "var(--fl-muted)" }}>{cms.text("why", "body", "Three principles shape everything we do.")}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {whyItems.filter((it) => it.visible).map((it, i) => {
                const Icon = WHY_ICONS[i] || Sparkles;
                return (
                  <div key={i} data-reveal data-testid={`why-point-${i}`}>
                    <div className="w-12 h-12 rounded-full grid place-items-center border border-[color:var(--fl-orange)]/30">
                      <Icon className="text-[color:var(--fl-orange)]" size={22} />
                    </div>
                    <div className="fl-heading text-2xl mt-5">{it.label}</div>
                    <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--fl-muted)" }}>{it.body}</p>
                  </div>
                );
              })}
            </div>
            {cms.text("why", "ctaLabel", "") && (
              <Link to={cms.get("why").ctaPath || "/services"} className="fl-btn-ghost mt-12 inline-flex" data-testid="why-learn-more">
                {cms.text("why", "ctaLabel", "")} <ArrowRight size={15} />
              </Link>
            )}
          </div>
        </section>
      ),
    },
    // ---- Connected care / web platform (no app claims) ----
    cms.isVisible("platform") && {
      key: "platform", default: 2,
      node: (
        <section key="platform" className="py-24 md:py-36 relative border-t border-white/5" style={{ background: "var(--fl-bg)" }} data-testid="home-section-platform">
          <div className="absolute top-0 left-0 right-0 h-px" style={topLine} />
          <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-5">
              <div className="fl-label text-[color:var(--fl-orange)] mb-3">{cms.text("platform", "eyebrow", "Connected care")}</div>
              <h2 data-reveal className="fl-heading text-4xl md:text-5xl leading-tight">{cms.text("platform", "heading", "Your programme. Your progress. Your clinician.")}</h2>
              <p className="mt-5 text-sm md:text-base leading-relaxed" style={{ color: "var(--fl-muted)" }}>{cms.text("platform", "body", "Your rehabilitation stays connected between appointments — through a secure digital platform that keeps you and your clinician on the same page.")}</p>
              <div className="mt-8 flex flex-wrap items-center gap-2">
                {platformSteps.filter((s) => s.visible).map((s, i) => (
                  <span key={i} className="inline-flex items-center gap-2" data-testid={`platform-step-${i}`}>
                    <span className="fl-label px-3 py-1.5 rounded-full border border-white/10 text-[color:var(--fl-text)]">{s.label}</span>
                    {i < platformSteps.filter((p) => p.visible).length - 1 && <ArrowRight size={14} className="text-[color:var(--fl-orange)]" />}
                  </span>
                ))}
              </div>
            </div>
            <div className="md:col-span-7">
              {platformImg ? (
                <img src={platformImg} alt="Functional LAB connected care" loading="lazy" className="rounded-lg border border-white/10 w-full object-cover" />
              ) : (
                <div className="fl-card p-6 md:p-8 relative overflow-hidden" data-testid="platform-mock">
                  <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 80% 0%, color-mix(in oklab, var(--fl-orange) 10%, transparent), transparent 60%)" }} />
                  <div className="relative grid grid-cols-2 gap-3">
                    {platformSteps.filter((s) => s.visible).map((s, i) => {
                      const Icon = STEP_ICONS[i] || Check;
                      return (
                        <div key={i} className="rounded-md border border-white/10 p-4">
                          <Icon className="text-[color:var(--fl-orange)]" size={18} />
                          <div className="fl-heading text-lg mt-3">{s.label}</div>
                          <div className="h-1.5 bg-white/10 rounded mt-3 overflow-hidden"><div className="h-full bg-[color:var(--fl-orange)]" style={{ width: `${40 + i * 18}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      ),
    },
    // ---- Choose your starting point ----
    cms.isVisible("starts") && {
      key: "starts", default: 3,
      node: (
        <section key="starts" className="py-24 md:py-36 relative border-t border-white/5" style={{ background: "var(--fl-surface, #121212)" }} data-testid="home-section-starts">
          <div className="absolute top-0 left-0 right-0 h-px" style={topLine} />
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-2xl mb-14">
              <div className="fl-label text-[color:var(--fl-orange)] mb-3">{cms.text("starts", "eyebrow", "Where do you fit?")}</div>
              <h2 data-reveal className="fl-heading text-4xl md:text-5xl leading-tight">{cms.text("starts", "heading", "Choose your starting point.")}</h2>
              <p className="mt-5 text-sm md:text-base" style={{ color: "var(--fl-muted)" }}>{cms.text("starts", "body", "Tell us where you are today and we'll point you to the right next step.")}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {startItems.filter((it) => it.visible).map((it, i) => {
                const Icon = START_ICONS[i] || Compass;
                return (
                  <Link key={i} to={it.ctaPath || BOOK} data-reveal className="fl-card p-7 flex flex-col group hover:border-[color:var(--fl-orange)]/40 transition-colors" data-testid={`start-card-${i}`}>
                    <Icon className="text-[color:var(--fl-orange)]" size={24} />
                    <div className="fl-heading text-xl mt-5 leading-snug">{it.label}</div>
                    <p className="mt-3 text-sm leading-relaxed flex-1" style={{ color: "var(--fl-muted)" }}>{it.body}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm text-[color:var(--fl-orange)] font-semibold">
                      {it.ctaLabel} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                );
              })}
            </div>
            <div className="mt-8 text-sm" style={{ color: "var(--fl-muted)" }}>
              Not sure? <Link to={FIND} className="text-[color:var(--fl-orange)] font-semibold hover:underline" data-testid="starts-finder-link">Find your service →</Link>
            </div>
          </div>
        </section>
      ),
    },
    // ---- Services & support options ----
    cms.isVisible("support") && {
      key: "support", default: 4,
      node: (
        <section key="support" id="support" className="py-24 md:py-36 relative border-t border-white/5" style={{ background: "var(--fl-bg)" }} data-testid="home-section-support">
          <div className="absolute top-0 left-0 right-0 h-px" style={topLine} />
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-2xl mb-14">
              <div className="fl-label text-[color:var(--fl-orange)] mb-3">{cms.text("support", "eyebrow", "How we can help")}</div>
              <h2 data-reveal className="fl-heading text-4xl md:text-5xl leading-tight">{cms.text("support", "heading", "Support that fits your stage.")}</h2>
              <p className="mt-5 text-sm md:text-base" style={{ color: "var(--fl-muted)" }}>{cms.text("support", "body", "From a single session to ongoing clinician-led care — pick the level of support that suits you.")}</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {supportItems.filter((it) => it.visible).map((it, i) => {
                const Icon = SUPPORT_ICONS[i] || Activity;
                return (
                  <div key={i} data-reveal className="fl-card p-7 flex flex-col" data-testid={`support-card-${i}`}>
                    <Icon className="text-[color:var(--fl-orange)]" size={22} />
                    <div className="fl-heading text-lg mt-5">{it.label}</div>
                    <p className="mt-2 text-sm leading-relaxed flex-1" style={{ color: "var(--fl-muted)" }}>{it.body}</p>
                    <Link to={it.ctaPath || "/prices"} className="fl-btn-ghost text-xs mt-6 justify-center" data-testid={`support-cta-${i}`}>{it.ctaLabel} <ArrowRight size={13} /></Link>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ),
    },
    // ---- Clinicians spotlight ----
    cms.isVisible("clinicians") && {
      key: "clinicians", default: 5,
      node: (
        <section key="clinicians" className="py-24 md:py-36 relative border-t border-white/5" style={{ background: "var(--fl-surface, #121212)" }} data-testid="home-section-clinicians">
          <div className="absolute top-0 left-0 right-0 h-px" style={topLine} />
          <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <img src={cliniciansImg} alt="Functional LAB clinicians" loading="lazy" className="rounded-lg border border-white/10 w-full object-cover" />
            </div>
            <div className="md:col-span-5">
              <div className="fl-label text-[color:var(--fl-orange)] mb-3">{cms.text("clinicians", "eyebrow", "The team")}</div>
              <h2 data-reveal className="fl-heading text-4xl md:text-5xl leading-tight">{cms.text("clinicians", "heading", "Treated by clinicians, not algorithms.")}</h2>
              <p className="mt-5 text-sm md:text-base leading-relaxed" style={{ color: "var(--fl-muted)" }}>{cms.text("clinicians", "body", "Qualified sport rehabilitators and physiotherapists lead every pathway — combining hands-on treatment with evidence-based progression.")}</p>
              <Link to={cms.get("clinicians").ctaPath || "/team"} className="fl-btn mt-8" data-testid="clinicians-cta"><Users size={16} /> {cms.text("clinicians", "ctaLabel", "Meet the team")}</Link>
            </div>
          </div>
        </section>
      ),
    },
    // ---- Proof / testimonials ----
    cms.isVisible("testimonials") && {
      key: "testimonials", default: 6,
      node: (
        <section key="testimonials" className="py-24 md:py-36 relative border-t border-white/5" style={{ background: "var(--fl-bg)" }} data-testid="home-section-testimonials">
          <div className="absolute top-0 left-0 right-0 h-px" style={topLine} />
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Quote className="text-[color:var(--fl-orange)] mx-auto" size={32} />
            <div className="fl-label text-[color:var(--fl-orange)] mt-5 mb-3">{cms.text("testimonials", "eyebrow", "Proof")}</div>
            <h2 data-reveal className="fl-heading text-3xl md:text-5xl leading-tight">{cms.text("testimonials", "heading", "Recovery that people trust.")}</h2>
            <p className="mt-5 text-sm md:text-base max-w-2xl mx-auto" style={{ color: "var(--fl-muted)" }}>{cms.text("testimonials", "body", "Patients and athletes come back to Functional LAB because the results — and the care — speak for themselves.")}</p>
            <Link to={cms.get("testimonials").ctaPath || "/testimonials"} className="fl-btn-ghost mt-8 inline-flex" data-testid="testimonials-cta">{cms.text("testimonials", "ctaLabel", "Read patient stories")} <ArrowRight size={15} /></Link>
          </div>
        </section>
      ),
    },
    // ---- Final CTA ----
    cms.isVisible("finalCta") && {
      key: "finalCta", default: 7,
      node: (
        <section key="finalCta" className="py-28 md:py-40 text-center relative border-t border-white/5" style={{ background: "linear-gradient(to bottom, var(--fl-bg), #170f0d, var(--fl-bg))" }} data-testid="home-section-finalCta">
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, color-mix(in oklab, var(--fl-orange) 55%, transparent), transparent)" }} />
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="fl-heading text-4xl md:text-6xl leading-tight">{cms.text("finalCta", "heading", "Your recovery doesn't stop when the appointment ends.")}</h2>
            <p className="mt-5 text-base md:text-lg" style={{ color: "var(--fl-muted)" }}>{cms.text("finalCta", "body", "Start with an assessment and we'll build the pathway that gets you back to what matters.")}</p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link to={cms.get("finalCta").ctaPath || BOOK} className="fl-btn" data-testid="final-cta-book">{cms.text("finalCta", "ctaLabel", "Book an assessment")} <ArrowRight size={16} /></Link>
              <Link to={cms.get("finalCta").ctaPath2 || FIND} className="fl-btn-ghost" data-testid="final-cta-find">{cms.text("finalCta", "ctaLabel2", "Find your starting point")}</Link>
            </div>
          </div>
        </section>
      ),
    },
  ].filter(Boolean);

  const ordered = SECTIONS
    .sort((a, b) => cms.orderOf(a.key, a.default) - cms.orderOf(b.key, b.default))
    .map((s) => s.node);

  return (
    <div className="text-[color:var(--fl-text)] min-h-screen fl-noise fl-page" style={{ background: "var(--fl-bg)" }}>
      <PublicHeader />

      {/* ============ Cinematic hero ============ */}
      {cms.isVisible("hero") && (
        <section className="relative min-h-[94vh] flex items-center pt-24 md:pt-28 overflow-hidden" data-testid="hero-section">
          <div className="absolute inset-0" ref={heroBgRef}>
            {heroVideo && !reduced ? (
              <video
                src={heroVideo}
                poster={heroImg}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-full object-cover opacity-45 fl-hero-img"
                data-testid="hero-video"
              />
            ) : (
              <img src={heroImg} alt="Athlete in rehabilitation" className="w-full h-full object-cover opacity-45 fl-hero-img fl-ken-burns" data-testid="hero-image" />
            )}
            <div className="absolute inset-0 fl-hero-scrim" />
          </div>

          <div className="relative max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-4xl">
              <div className="fl-label mb-5 text-[color:var(--fl-orange)]">{cms.text("hero", "eyebrow", t("hero.eyebrow"))}</div>
              <h1 className="fl-heading text-5xl md:text-7xl lg:text-8xl leading-[0.92] uppercase">
                <span className="block" data-testid="hero-line-1">{cms.text("hero", "heading", t("hero.title1"))}</span>
                <span className="block text-[color:var(--fl-orange)]" data-testid="hero-line-2">{cms.text("hero", "accentText", t("hero.title2"))}</span>
                <span className="block" data-testid="hero-line-3">{cms.text("hero", "subheading", t("hero.title3"))}</span>
              </h1>
              <p className="mt-8 text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed">
                {cms.text("hero", "body", "From assessment to performance, your rehabilitation stays connected.")}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to={cms.get("hero").ctaPath || BOOK} className="fl-btn" data-testid="hero-book">{cms.text("hero", "ctaLabel", "Book an assessment")} <ArrowRight size={16} /></Link>
                <Link to={cms.get("hero").ctaPath2 || FIND} className="fl-btn-ghost" data-testid="hero-find">{cms.text("hero", "ctaLabel2", "Find my starting point")}</Link>
                <WhatsAppCTA message={DEFAULT_WA_MESSAGES.hero} variant="soft" testid="hero-wa">{t("pub.wa.needHelp")}</WhatsAppCTA>
              </div>
            </div>
          </div>

          <a href="#journey" className={`fl-scroll-cue ${scrolled ? "hidden" : ""} absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 group`}
            style={{ color: "var(--fl-muted)" }} data-testid="hero-scroll-cue" aria-label={t("hero.scrollAria", "Scroll to next section")}>
            <span className="fl-label tracking-[0.35em] group-hover:text-[color:var(--fl-orange)] transition-colors">{t("hero.scroll", "Scroll")}</span>
            <ChevronDown size={18} className="group-hover:text-[color:var(--fl-orange)] transition-colors" />
          </a>
        </section>
      )}

      {ordered}

      <PublicFooter />
    </div>
  );
}
